import React, { useContext, useEffect, useMemo, useState } from "react";
import { ConstantsContext } from "../index";
import CoinLogo from "./CoinLogo";

export default function NewsBriefTable({
  title = "Crypto News",
  initialLimit = 3,
  _apiConnect, // optional: use your transport helper if passed
}) {
  const [staticText] = useContext(ConstantsContext);
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [lastUpdatedAgo, setLastUpdatedAgo] = useState(null);
  const [sortMode, setSortMode] = useState("alpha"); // "alpha" | "latest" | "sentiment"

  const endpoint = (staticText && staticText.path_darts) || "/";

  // fetch news
  useEffect(() => {
    const handleResponse = (json) => {
      // API payload: { status_code: 2, status: true, news: [...] }
      const raw = Array.isArray(json?.news)
        ? json.news
        : Array.isArray(json)
        ? json
        : Object.values(json?.news || {});

      const normalized = (raw || []).map((it, idx) => {
        const pair =
          it.pair ||
          it.coin_pair_name ||
          (it.coin ? `${String(it.coin).toUpperCase()}-USD` : null);
        const coin = it.coin || (pair ? String(pair).split("-")[0] : null);

        return {
          id: it.id ? it.id : idx,
          pair,
          coin,
          headline: it.headline ? it.headline : "No material news found",
          source: it.source && it.source !== "N/A" ? it.source : "—",
          headline_sentiment: String(
            it.headline_sentiment || "neutral"
          ).toLowerCase(),
          reason: it.reason ? it.reason : "—",
          dateAgo: it.dateAgo || it.date_ago || "—",
          url: it.url && it.url !== "N/A" ? it.url : null,
          hasNews:
            it.hasNews === true ||
            it.hasNews === 1 ||
            it.hasNews === "1" ||
            (it.url && it.url !== "N/A"),
        };
      });

      setRows(normalized);
      setLastUpdatedAgo(
        (normalized.find((x) => x.dateAgo && x.dateAgo !== "—") || {}).dateAgo ||
          "just now"
      );
      setExpanded(false);
    };

    if (typeof _apiConnect === "function") {
      const data = [{ key: "action", value: "get_crypto_news" }];
      _apiConnect(endpoint, data, handleResponse);
    } else {
      const fd = new FormData();
      fd.append("action", "get_crypto_news");
      fetch(endpoint, { method: "POST", body: fd })
        .then((r) => r.json())
        .then(handleResponse)
        .catch(() => setRows([]));
    }
  }, [endpoint, _apiConnect]);

  // sorting (like your News section)
  const sorted = useMemo(() => {
    const list = [...rows];
    if (sortMode === "alpha") {
      list.sort((a, b) => {
        const A = (a.pair || "").localeCompare(b.pair || "");
        if (A !== 0) return A;
        // no reliable date field present—keep original order otherwise
        return 0;
      });
    } else if (sortMode === "latest") {
      // We only have relative "dateAgo" strings; keep API order as "latest"
      // If you add a timestamp later, sort by that here.
      return list;
    } else if (sortMode === "sentiment") {
      const rank = { bullish: 0, neutral: 1, bearish: 2 };
      list.sort((a, b) => {
        const ra = rank[a.headline_sentiment] ? rank[a.headline_sentiment] : 1;
        const rb = rank[b.headline_sentiment] ? rank[b.headline_sentiment] : 1;
        return ra - rb;
      });
    }
    return list;
  }, [rows, sortMode]);

  const visible = useMemo(
    () => (expanded ? sorted : sorted.slice(0, initialLimit)),
    [sorted, expanded, initialLimit]
  );

  const sentimentInfo = (v) => {
    const s = String(v || "").toLowerCase();
    if (s === "bullish") return { label: "Bullish", color: "#10b981" };
    if (s === "bearish") return { label: "Bearish", color: "#ef4444" };
    return { label: "Neutral", color: "#94a3b8" };
  };

  const safe = (v) => (v === null || v === undefined || v === "" ? "—" : v);

  return (
    <section className="relative">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        {/* Title + updated + sort pills */}
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold tracking-tight">
            {title}{" "}
            {lastUpdatedAgo ? (
              <span className="ml-2 text-xs text-slate-400">
                {lastUpdatedAgo} ago
              </span>
            ) : null}
          </h3>

          <div className="flex items-center gap-1 text-xs">
            <span className="hidden sm:inline text-slate-400 mr-1">Sort by:</span>
            {[
              { key: "alpha", label: "Alphabetical" },
              { key: "latest", label: "Latest" },
              { key: "sentiment", label: "Sentiment" },
            ].map((o) => (
              <button
                key={o.key}
                onClick={() => setSortMode(o.key)}
                className={`rounded-full px-3 py-1 border border-white/10 ${
                  sortMode === o.key
                    ? "bg-white/10"
                    : "bg-transparent hover:bg-white/10"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Desktop/tablet: table (md and up) */}
        <div className="hidden md:block rounded-2xl border border-white/10 bg-white/5 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-900/60 backdrop-blur">
              <tr className="text-left text-slate-300">
                <th className="px-4 py-3 font-medium">Coin</th>
                <th className="px-4 py-3 font-medium">Headline</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Impact</th>
                <th className="px-4 py-3 font-medium">Reason</th>
                <th className="px-4 py-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((n) => {
                const s = sentimentInfo(n.headline_sentiment);
                const dim = n.hasNews === false;
                return (
                  <tr
                    key={n.id}
                    className={`border-t border-white/5 ${dim ? "opacity-70" : ""}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <CoinLogo coinPair={n.pair || `${n.coin}-USD`} />
                        <div className="font-medium">
                          {n.pair || `${n.coin}-USD`}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="text-slate-100">{safe(n.headline)}</div>
                    </td>

                    <td className="px-4 py-3">{safe(n.source)}</td>

                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2">
                        <span
                          aria-hidden
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: s.color,
                            display: "inline-block",
                          }}
                        />
                        <span>{s.label}</span>
                      </span>
                    </td>

                    <td className="px-4 py-3 text-slate-300">{safe(n.reason)}</td>

                    <td className="px-4 py-3 whitespace-nowrap">{safe(n.dateAgo)}</td>

                  </tr>
                );
              })}
            </tbody>
          </table>

          {sorted.length > initialLimit && (
            <div className="px-4 py-3">
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-xs text-emerald-400 hover:text-emerald-300"
              >
                {expanded ? "Show less" : `Show ${sorted.length - initialLimit} more`}
              </button>
            </div>
          )}
        </div>

        {/* Mobile: cards (below md) */}
        <div className="md:hidden space-y-3">
          {visible.map((n) => {
            const s = sentimentInfo(n.headline_sentiment);
            const dim = n.hasNews === false;
            return (
              <div
                key={n.id}
                className={`rounded-2xl border border-white/10 bg-white/5 p-4 ${
                  dim ? "opacity-75" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <CoinLogo coinPair={n.pair || `${n.coin}-USD`} />
                    <div className="font-semibold">
                      {n.pair || `${n.coin}-USD`}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">{safe(n.dateAgo)}</div>
                </div>

                <div className="mt-2 text-sm text-slate-100">
                  {safe(n.headline)}
                </div>

                <div className="mt-2 flex items-center gap-3 text-xs">
                  <span className="text-slate-400">Source:</span>
                  <span className="text-slate-200">{safe(n.source)}</span>
                </div>

                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: s.color,
                      display: "inline-block",
                    }}
                  />
                  <span className="font-medium">{s.label}</span>
                </div>

                <div className="mt-2 text-xs text-slate-300">
                  {safe(n.reason)}
                </div>

              </div>
            );
          })}

          {sorted.length > initialLimit && (
            <div>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-xs text-emerald-400 hover:text-emerald-300"
              >
                {expanded ? "Show less" : `Show ${sorted.length - initialLimit} more`}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
